import React from 'react';
import PropTypes from 'prop-types';
import tracking from 'dibs-tracking';
import { connect } from 'react-redux';
import { getEverLoggedIn } from 'dibs-cookie-jar';
import { trackChatOpen } from './helpers/tracking';
import { SESSION_CHAT_STATE_KEY } from './constants';
import { sessionStorage } from 'dibs-browser-storage';
import { graphql, createFragmentContainer } from 'react-relay';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';

// components
import { PdpRespDealerChatLazy } from './PdpRespDealerChatLazy';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

const PDP_DEALER_CHAT_TEST = 'PDP_DEALER_CHAT_TEST';
const PDP_DEALER_CHAT_TEST_2 = 'PDP_DEALER_CHAT_TEST_2';

export const PdpRespDealerChatLoaderComponent = ({
    item,
    user,
    environment,
    isPdpDealerChatTest,
}) => {
    const userId = user?.serviceId;
    const isEmailOnlyUser = React.useRef(true);
    const chatVisibilityRef = React.useRef(null);
    const [isMinimized, setIsMinimized] = React.useState(false);
    const [isChatVisible, setIsChatVisible] = React.useState(false);
    const [shouldLoadChat, setShouldLoadChat] = React.useState(false);

    // AB test tracking
    React.useEffect(() => {
        tracking.trackAbTestEvent(
            getAbTestTrackingData(document.cookie, PDP_DEALER_CHAT_TEST, 'PDP_PAGEVIEW'),
            'pdp_pageview'
        );
        tracking.trackAbTestEvent(
            getAbTestTrackingData(document.cookie, PDP_DEALER_CHAT_TEST_2, 'PDP_PAGEVIEW'),
            'pdp_pageview'
        );
        isEmailOnlyUser.current = isEmailOnly(document.cookie);
    }, []);

    React.useEffect(() => {
        if (shouldLoadChat && isChatVisible && isPdpDealerChatTest) {
            trackChatOpen();
        }
    }, [shouldLoadChat, isChatVisible, isPdpDealerChatTest]);

    React.useEffect(() => {
        const { isClosed = false, isMinimized: isInitiallyMinimized = false } =
            sessionStorage.getItem(SESSION_CHAT_STATE_KEY) || {};
        const eliTrade = getEverLoggedIn(document.cookie) === 'trade';

        setIsMinimized(isInitiallyMinimized);

        /**
         * Load chat if:
         * 1. user has not closed the chat in this browser session
         * 2. user is not an email only user
         * 3. user is logged in user was never logged in as a trade user
         */

        setShouldLoadChat(!isClosed && !isEmailOnlyUser.current && (userId || !eliTrade));
    }, [userId]);

    const handleVisibilityChange = React.useCallback(
        ({ isVisible, boundingClientRect, disconnect }) => {
            if (isVisible || boundingClientRect?.top < 0) {
                setIsChatVisible(true);
                disconnect();

                // AB test tracking
                tracking.trackAbTestEvent(
                    getAbTestTrackingData(
                        document.cookie,
                        PDP_DEALER_CHAT_TEST,
                        'PDP_CHATBOX_TRIGGER'
                    ),
                    'pdp_chatbox_trigger'
                );
                tracking.trackAbTestEvent(
                    getAbTestTrackingData(
                        document.cookie,
                        PDP_DEALER_CHAT_TEST_2,
                        'PDP_CHATBOX_TRIGGER'
                    ),
                    'pdp_chatbox_trigger'
                );
            }
        },
        []
    );

    return (
        <>
            <div ref={chatVisibilityRef} />
            <VisibilityTracker
                elementRef={chatVisibilityRef}
                onVisibilityChange={handleVisibilityChange}
            />
            {shouldLoadChat && isChatVisible && isPdpDealerChatTest && (
                <PdpRespDealerChatLazy
                    item={item}
                    user={user}
                    environment={environment}
                    isInitiallyMinimized={isMinimized}
                />
            )}
        </>
    );
};

PdpRespDealerChatLoaderComponent.propTypes = {
    item: PropTypes.object,
    user: PropTypes.object,
    isPdpDealerChatTest: PropTypes.bool,
    environment: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
    const {
        relayVariables: { variables },
    } = state;

    return {
        isPdpDealerChatTest: variables?.isPdpDealerChatTest,
    };
}

export const PdpRespDealerChatLoader = createFragmentContainer(
    connect(mapStateToProps)(PdpRespDealerChatLoaderComponent),
    {
        item: graphql`
            fragment PdpRespDealerChatLoader_item on Item {
                ...PdpRespDealerChat_item @include(if: $isPdpDealerChatTest)
            }
        `,
        user: graphql`
            fragment PdpRespDealerChatLoader_user on User {
                serviceId
                ...PdpRespDealerChat_user @include(if: $isPdpDealerChatTest)
            }
        `,
    }
);
